import { useInfiniteQuery, type UseInfiniteQueryResult } from "@tanstack/react-query";
import { AxiosPromise } from "axios";
import { type ButtonTypes } from "components/Atoms/Button";
import ButtonGroup from "components/Atoms/ButtonGroup";
import { SomethingUnexpectedHappenedMessageBox } from "components/MessageBox/SomethingUnexpectedHappenedMessageBox";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import { SelectableListKeyboardNavigationGroup } from "keyboardNavigation/KeyboardNavigationPresets";
import React from "react";
import { Message } from "components/Atoms/Message";
import { useInView } from "react-intersection-observer";
import { useDebounce } from "utilities/usehooks";
import { getAbsoluteCountFromPaginatedInfiniteQueryData, getPaginatedInfiniteQueryData } from "./utils";
import { SearchInput } from "components/Atoms/SearchInput";

interface FilterButtonType {
  type: ButtonTypes
  isActive: boolean
  label: string
  group: string
  onClick: () => void
}

export type HasKeywordAndPagination = server.dto.IPaginationParameters & { keyword?: string };
export interface ModalPickerProps<TItem, TId extends number | string, TInstruction extends HasKeywordAndPagination> {
  instruction: TInstruction
  onInstructionChange: (instruction: TInstruction) => void
  queryKey: string;
  api: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<TItem[]>>;
  onClick: (item: TItem) => void

  excludeKeys?: TId[]

  renderListItem: (item: TItem) => JSX.Element
  getId: (item: TItem) => TId
  getError: (query: UseInfiniteQueryResult<server.dto.ApiResponseWrapper<TItem[]>, unknown>) => string

  filter1?: {
    leftButton: Omit<FilterButtonType, "group">
    rightButton: Omit<FilterButtonType, "group">
  }
  filter2?: {
    leftButton: Omit<FilterButtonType, "group">
    rightButton: Omit<FilterButtonType, "group">
  }
  hideKeywordFilter?: boolean
  otherFilters?: JSX.Element;
  isMult?: boolean;

  map?: (items: TItem[], searchQuery: string) => TItem[]

  renderBottomPart?: (displaySimplifiedView: boolean) => JSX.Element
}

const FilterButton = (props: FilterButtonType) => {
  return <ButtonGroup.Button
    group={props.group}
    active={props.isActive}
    onClick={props.onClick}
    type="secondary"
  >{props.label}</ButtonGroup.Button>;
};

const PickerList = <TItem extends unknown, TId extends number | string, TInstruction extends HasKeywordAndPagination>(props: ModalPickerProps<TItem, TId, TInstruction>) => {
  const { ref, inView } = useInView();

  const debouncedInstruction = useDebounce(props.instruction);

  const query = useInfiniteQuery({
    keepPreviousData: true,
    queryKey: [props.queryKey, debouncedInstruction],
    queryFn: async ({ pageParam = 1 }) => props.api({ ...debouncedInstruction, page: pageParam }).then(r => r.data),
    getNextPageParam: (lastPage) => lastPage.pagination.page !== lastPage.pagination.pageCount ? lastPage.pagination.page + 1 : undefined,
  });

  React.useEffect(() => {
    if (inView && query.hasNextPage && !query.isFetchingNextPage && !query.isFetching) {
      query.fetchNextPage();
    }
  }, [inView, query.hasNextPage, query.isFetchingNextPage, query.isFetching]);

  const excludeKeyMap = new Set(props.excludeKeys);

  const data = getPaginatedInfiniteQueryData(query);
  const displaySimplifiedView = getAbsoluteCountFromPaginatedInfiniteQueryData(query) < 10 && props.isMult;

  let filteredData = data.filter(i => !excludeKeyMap.has(props.getId(i)));

  if (props.map) {
    filteredData = props.map(filteredData, debouncedInstruction.keyword || "");
  }

  let state: "noResult" | "networkError" | "apiError" | "showResults" | "initialLoading";
  if (query.isError) {
    state = "networkError";
  } else if (props.getError(query)) {
    state = "apiError";
  } else if (query.isLoading && filteredData.length === 0) {
    state = "initialLoading";
  } else if (!displaySimplifiedView && filteredData.length === 0) {
    state = "noResult";
  } else if (displaySimplifiedView && data.length === 0) {
    state = "noResult";
  } else {
    state = "showResults";
  }

  const displayLoadNext = !query.isFetchingNextPage && !query.isFetching && filteredData.length > 0 && query.hasNextPage;

  return <>
    {(displaySimplifiedView && props.filter1 != null) && <div>
      <ButtonGroup.Group>
        <FilterButton
          group="filter1"
          type={props.filter1.leftButton.type}
          label={props.filter1.leftButton.label}
          onClick={props.filter1.leftButton.onClick}
          isActive={props.filter1.leftButton.isActive}
        />
        <FilterButton
          group="filter1"
          type={props.filter1.rightButton.type}
          label={props.filter1.rightButton.label}
          onClick={props.filter1.rightButton.onClick}
          isActive={props.filter1.rightButton.isActive}
        />
      </ButtonGroup.Group>
    </div>}

    {!displaySimplifiedView && <>
      <div className="flex-column gap">
        {props.filter1 != null && <div>
          <ButtonGroup.Group>
            <FilterButton
              group="filter1"
              type={props.filter1.leftButton.type}
              label={props.filter1.leftButton.label}
              onClick={props.filter1.leftButton.onClick}
              isActive={props.filter1.leftButton.isActive}
            />
            <FilterButton
              group="filter1"
              type={props.filter1.rightButton.type}
              label={props.filter1.rightButton.label}
              onClick={props.filter1.rightButton.onClick}
              isActive={props.filter1.rightButton.isActive}
            />
          </ButtonGroup.Group>
        </div>}
        {props.filter2 != null && <div>
          <ButtonGroup.Group>
            <FilterButton
              group="filter2"
              type={props.filter2.leftButton.type}
              label={props.filter2.leftButton.label}
              onClick={props.filter2.leftButton.onClick}
              isActive={props.filter2.leftButton.isActive}
            />
            <FilterButton
              group="filter2"
              type={props.filter2.rightButton.type}
              label={props.filter2.rightButton.label}
              onClick={props.filter2.rightButton.onClick}
              isActive={props.filter2.rightButton.isActive}
            />
          </ButtonGroup.Group>
        </div>}
      </div>

      {props.otherFilters}

      {props.hideKeywordFilter !== true && <div>
        <SearchInput
          focusOnMount
          value={props.instruction.keyword}
          onChange={value => {
            props.onInstructionChange({ ...props.instruction, keyword: value, page: 1 });
          }}
          placeholder={RESX.GeneralLabels.SearchByName}
        />
      </div>}
    </>}

    <div className="flex-column gap-small scrollbox-auto grow1" style={{ position: "relative" }}>
      {state === "networkError" && <SomethingUnexpectedHappenedMessageBox />}
      {state === "apiError" && <Message type="error"> {props.getError(query)}</Message>}
      {state === "noResult" && <NoDataAvailableDiv />}
      {state === "initialLoading" && <Loading />}
      {!displaySimplifiedView && filteredData.map((item, index) => <div
        ref={(index === filteredData.length - 5 && displayLoadNext) ? ref : undefined}
        tabIndex={0}
        {...SelectableListKeyboardNavigationGroup("pickerList")}
        key={props.getId(item)}
        className={props.isMult ? "padding-top-small" : "s1_selectmenu_item gray-100 focusable hoverable padding"}
        onClick={() => props.onClick(item)}
      >
        {props.renderListItem(item)}
      </div>)}
      {displaySimplifiedView && data.map(item => <div
        tabIndex={0}
        {...SelectableListKeyboardNavigationGroup("pickerList")}
        key={props.getId(item)}
        className="padding-top-small"
      >
        {props.renderListItem(item)}
      </div>)}
    </div>

    {props.renderBottomPart?.(displaySimplifiedView)}
  </>;
};

export default PickerList;